import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const pageSidebarSlotRecipe = defineSlotRecipe({
  className: 'page-sidebar',
  slots: [
    'body',
    'contentBlock',
    'divider',
    'footer',
    'item',
    'itemIcon',
    'itemLabel',
    'progressBlocker',
    'progressConnector',
    'progressContentBlock',
    'progressIcon',
    'progressLabel',
    'progressRow',
    'progressSecondaryText',
    'progressTextBlock',
    'project',
    'projectLabel',
    'projectValue',
    'projectValueWrapper',
    'root',
    'selectionMarker',
    'suffixIcon',
    'title',
    'titleBlock',
    'titleText',
  ],
  base: {
    root: {
      background: 'background/primary',
      // Longhands, not the `borderInlineEnd: '1px solid'` shorthand: that shorthand carries no
      // colour, so it resets it to `currentColor` if it lands after `borderColor` — which is what
      // sorting these keys alphabetically did, turning the rule dark.
      borderInlineEndColor: 'border/regular',
      borderInlineEndStyle: 'solid',
      borderInlineEndWidth: '1',
      display: 'flex',
      flexDirection: 'column',
      // `minHeight` rather than `height`: on a short navigation the sidebar still reaches the
      // bottom of the viewport, but a long one grows past it and scrolls with the page as one
      // block — the footer is part of that scroll, not pinned to the bottom.
      minHeight: '100dvh',
      // Below `tablet` the sidebar is the whole view — Figma's mobile frame (`23056:50745`) is the
      // full 375 of the phone, not a 240 column beside a page.
      maxWidth: { base: 'none', tablet: rem(320) },
      minWidth: rem(240),
      paddingBlockStart: '16',
      width: { base: 'full', tablet: rem(240) },
    },
    body: {
      display: 'flex',
      // Absorbs the slack so the footer sits at the bottom when the navigation is short, and
      // simply grows when it is long.
      flex: 1,
      flexDirection: 'column',
      // Half of the gap to the footer block; the footer carries the other half. Split that way so
      // this one is also the sidebar's bottom padding when there is no footer -- 8 either way,
      // rather than the body's whole 16 becoming the bottom edge. It is a margin so it stays
      // outside the items' own box, and `flex: 1` above means any extra space lands in the body
      // rather than widening the gap. Margins do not collapse in a flex column, so the two add up.
      marginBlockEnd: '8',
      width: 'full',
    },
    title: {
      alignItems: 'center',
      color: 'text/primary',
      display: 'flex',
      flexShrink: 0,
      overflow: 'clip',
      paddingInlineEnd: '32',
    },
    titleBlock: {
      alignItems: 'center',
      display: 'flex',
      minWidth: 0,
    },
    titleText: {
      textStyle: 'heading/h3',
      whiteSpace: 'nowrap',
    },
    item: {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      focusVisibleRing: 'inside',
      gap: '12',
      overflow: 'clip',
      // 48 tall on mobile against 40 on desktop — the row is a touch target there.
      paddingBlock: { base: '12', tablet: '8' },
      paddingInline: '24',
      position: 'relative',
      width: 'full',
      // Mobile reaches for `background/hover` / `background/active` where desktop uses the
      // `secondary` / `tertiary` surfaces. The two happen to resolve close, but they are separate
      // tokens in the design and the mobile item (`9221:4406`) binds these.
      _hover: {
        background: { base: 'background/hover', tablet: 'background/secondary' },
        _active: {
          background: { base: 'background/active', tablet: 'background/tertiary' },
        },
      },
      _active: {
        background: { base: 'background/active', tablet: 'background/tertiary' },
      },
      // `focusVisibleRing: 'inside'` still resolves to `outline-offset: 0`, which draws the ring
      // just outside the border box. The item spans the sidebar's full width, so that would bleed
      // past the sidebar's edges — pull the whole ring inside the item instead.
      _focusVisible: {
        outlineOffset: 'calc(var(--focus-ring-width) * -1)',
      },
    },
    contentBlock: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: '12',
      minWidth: 0,
      // Pads the 16px icon out to the 24px line box on desktop. The mobile icon is already 24, so
      // there is nothing to make up — the row would just grow past 48.
      paddingBlock: { base: '0', tablet: '2' },
    },
    itemIcon: {
      color: 'icon/secondary',
      flexShrink: 0,
      _groupHover: {
        color: 'icon/primary',
      },
    },
    itemLabel: {
      color: 'text/primary',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textAlign: 'start',
      textOverflow: 'ellipsis',
      textStyle: { base: 'body/lg/regular', tablet: 'body/md/regular' },
      whiteSpace: 'nowrap',
    },
    suffixIcon: {
      color: 'icon/tertiary',
      flexShrink: 0,
    },
    selectionMarker: {
      background: 'border/selected',
      // Mobile draws the marker as a plain 4px edge (`9221:4417`); desktop as a 2px rounded rule.
      borderEndStartRadius: { base: '0', tablet: '2' },
      borderStartStartRadius: { base: '0', tablet: '2' },
      height: 'full',
      insetBlockStart: 0,
      insetInlineEnd: 0,
      position: 'absolute',
      width: { base: '4', tablet: '2' },
    },
    footer: {
      borderBlockStart: '1px solid',
      borderColor: 'border/minimal',
      display: 'flex',
      flexDirection: 'column',
      // The body above absorbs the free space, so the footer needs no positioning of its own —
      // it just must not be squashed by a long list.
      flexShrink: 0,
      // The other half of the gap to the body — see its `marginBlockEnd`.
      marginBlockStart: '8',
      paddingBlock: '8',
    },
    /**
     * The progress item's own text, rather than `itemLabel`: that slot grows to fill the row, which
     * here belongs to the blocker so the connector can run to the trailing edge.
     */
    progressLabel: {
      color: 'text/primary',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textStyle: { base: 'body/lg/regular', tablet: 'body/md/regular' },
      whiteSpace: 'nowrap',
    },
    /** Follows the label after a middle dot, which the component renders. */
    progressSecondaryText: {
      color: 'text/tertiary',
      flexShrink: 0,
      textStyle: { base: 'body/lg/regular', tablet: 'body/md/regular' },
      whiteSpace: 'nowrap',
    },
    /**
     * Badge and connector as one unit, taking the width the text leaves.
     *
     * `fit-content` rather than 0: the flex basis is 0, so with a shrinking text block beside it
     * there is no free space to grow into and the block would be handed the whole row -- leaving
     * the badge, which does not shrink, hanging off the trailing edge. The floor is the badge; the
     * connector inside is basis 0 and contributes nothing to it.
     */
    progressBlocker: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: '12',
      minWidth: 'fit-content',
    },
    /**
     * The status icon's own slot rather than `itemIcon`: that one brightens to `icon/primary` with
     * the row, which is right for navigation and wrong here -- hovering a stage would drop the
     * colour that says where it stands.
     */
    progressIcon: {
      // The colour a status-less icon takes -- the approvals step's, which the design draws in the
      // same `icon/secondary` as the navigation icons. `iconColor` overrides it per stage.
      color: 'icon/secondary',
      flexShrink: 0,
      // Its own slot rather than `itemIcon`, but it does brighten with the row: the design moves the
      // icon to `icon/primary` on hover and active. That only ever reaches a colour this recipe
      // supplied -- a consumer's `iconColor` is an inline, unlayered `color` and beats it -- so a
      // stage's status survives the hover while a status-less icon follows the row.
      _groupHover: {
        color: 'icon/primary',
      },
    },
    /**
     * Label and secondary text read as one line, so they sit closer than the row's own 12 gap.
     *
     * It shrinks before the blocker does: `item` clips its overflow, so without this a long stage
     * name is cut mid-word and pushes the secondary text and the connector off the row. The
     * blocker keeps `flex: 1`, so it still takes the slack whenever the text is short enough.
     */
    progressTextBlock: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 1,
      gap: '4',
      minWidth: 0,
    },
    /**
     * The progress row's block padding, which does not follow the navigation row's to 12 on mobile:
     * the design keeps this row on 8 at both widths, so it is 44 tall on mobile against a nav row's
     * 48. Layered over `item` rather than replacing it -- everything else about the row is shared.
     */
    progressRow: {
      paddingBlock: '8',
    },
    /** With the row above: 2 either side of the line box at both widths, so the block is 28. */
    progressContentBlock: {
      paddingBlock: '2',
    },
    progressConnector: {
      borderColor: 'border/minimal',
      borderStyle: 'dashed',
      flex: 1,
    },
    project: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4',
      justifyContent: 'center',
      overflow: 'clip',
      paddingBlockEnd: { base: '12', tablet: '8' },
      paddingBlockStart: '8',
      paddingInlineEnd: { base: '24', tablet: '32' },
      paddingInlineStart: '24',
      whiteSpace: 'nowrap',
    },
    projectLabel: {
      color: 'text/tertiary',
      textStyle: 'heading/h6',
    },
    projectValue: {
      color: 'text/body',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textStyle: 'body/lg/semibold',
    },
    projectValueWrapper: {
      display: 'flex',
      minWidth: 0,
    },
    divider: {
      // Both of the sidebar's separator roles are `border/minimal` — the group header
      // (`20014:6508`) and this plain rule (`20014:6511`) — against `border/regular` for the general
      // Separator component. The override is per-slot for that reason, not to limit blast radius.
      borderColor: 'border/minimal',
      // Mobile's separator row is 40 tall, which centres the rule at 19.5 a side; 20 is the token
      // next to it and the resulting 1px lands nowhere the eye can find it.
      marginBlock: { base: '20', tablet: '16' },
      marginInline: '24',
      width: 'auto',
    },
  },
  variants: {
    hasBack: {
      // Redlines on the sub view (Figma `- page sidebar - interactions`): the title block sits at
      // x/y 16, is 40 tall, and the 32px back button sits at y 20 — i.e. the 4px padding belongs to
      // the block wrapping button + text, not to the text. Header totals 16 + 40 + 8 = 64.
      true: {
        title: {
          paddingBlockEnd: '8',
          paddingInlineStart: '16',
        },
        titleBlock: {
          gap: '4',
          paddingBlock: '4',
        },
      },
      // Main view: no button, so the text alone is the block — 6 + 28 + 6 = 40, header 16+40+16 = 72.
      false: {
        title: {
          paddingBlockEnd: '16',
          paddingInlineStart: '24',
        },
        titleBlock: {
          paddingBlock: '6',
        },
      },
    },
    isDisabled: {
      true: {
        item: {
          cursor: 'not-allowed',
          _hover: {
            background: 'transparent',
            _active: {
              background: 'transparent',
            },
          },
          _active: {
            background: 'transparent',
          },
        },
        itemIcon: {
          color: 'icon/disabled',
          _groupHover: {
            color: 'icon/disabled',
          },
        },
        itemLabel: {
          color: 'text/disabled',
        },
        progressIcon: {
          color: 'icon/disabled',
          _groupHover: {
            color: 'icon/disabled',
          },
        },
        progressLabel: {
          color: 'text/disabled',
        },
        progressSecondaryText: {
          color: 'text/disabled',
        },
      },
      false: {},
    },
    selected: {
      true: {
        item: {
          background: 'background/selected',
          _hover: {
            background: 'background/selected-hover',
            _active: {
              background: { base: 'background/selected-hover', tablet: 'background/selected-active' },
            },
          },
          _active: {
            background: { base: 'background/selected-hover', tablet: 'background/selected-active' },
          },
        },
        itemIcon: {
          color: 'icon/interactive',
          _groupHover: {
            color: 'icon/interactive-hover',
          },
        },
        itemLabel: {
          textStyle: { base: 'body/lg/semibold', tablet: 'body/md/semibold' },
        },
        progressIcon: {
          color: 'icon/interactive',
          _groupHover: {
            color: 'icon/interactive-hover',
          },
        },
        progressLabel: {
          textStyle: { base: 'body/lg/semibold', tablet: 'body/md/semibold' },
        },
      },
      false: {},
    },
    /**
     * A stage the release does not have to clear. Everything steps down one level -- the icon to
     * tertiary, the label to secondary -- so the row reads as present but not awaited. Hover and
     * active take both back to the row's ordinary colours, as they do for a required stage.
     *
     * `selected` wins over it, resolved in the component rather than here. The design draws the
     * pair (`selected=yes, notRequired=true`) and gives it the selected row's icon and label with
     * the `not required` secondary text kept, which is what dropping this variant produces.
     */
    notRequired: {
      true: {
        progressIcon: {
          color: 'icon/tertiary',
          _groupHover: { color: 'icon/primary' },
        },
        progressLabel: {
          color: 'text/secondary',
          _groupHover: { color: 'text/primary' },
        },
      },
      false: {},
    },
  },
  defaultVariants: {
    hasBack: false,
    isDisabled: false,
    notRequired: false,
    selected: false,
  },
});

export default pageSidebarSlotRecipe;
